import { c as _c } from "react/compiler-runtime";
import { DynamicCodeBlock } from "fumadocs-ui/components/dynamic-codeblock";
import defaultMdxComponents from "fumadocs-ui/mdx";
import { toJsxRuntime } from "hast-util-to-jsx-runtime";
import { Children, type ComponentProps, type ReactElement, type ReactNode, Suspense, use, useDeferredValue } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { remark } from "remark";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import { visit } from "unist-util-visit";
import type { ElementContent, Root, RootContent } from "hast";
export interface Processor {
  process: (content: string) => Promise<ReactNode>;
}
export function rehypeWrapWords() {
  return (tree: Root) => {
    visit(tree, (node, index, parent) => {
      if (node.type === "element" && node.tagName === "pre") {
        return "skip";
      }
      if (node.type !== "text" || !parent || index === undefined) {
        return;
      }
      const words: string[] = node.value.split(/(?=\s)/);

      // Create new span nodes for each word and whitespace
      const newNodes: ElementContent[] = words.flatMap((word: string) => {
        if (word.length === 0) {
          return [];
        }
        return {
          type: "element",
          tagName: "span",
          properties: {
            class: "animate-fd-fade-in"
          },
          children: [{
            type: "text",
            value: word
          }]
        };
      });
      Object.assign(node, {
        type: "element",
        tagName: "span",
        properties: {},
        children: newNodes
      } satisfies RootContent);
      return "skip";
    });
  };
}
function createProcessor(): Processor {
  const processor = remark().use(remarkGfm).use(remarkRehype).use(rehypeWrapWords);
  return {
    async process(content) {
      const nodes = processor.parse({
        value: content
      });
      const hast = await processor.run(nodes);
      return toJsxRuntime(hast, {
        development: false,
        jsx,
        jsxs,
        Fragment,
        components: {
          ...defaultMdxComponents,
          pre: Pre,
          img: undefined // use JSX
        }
      });
    }
  };
}
function Pre(props) {
  const $ = _c(9);
  let T0;
  let t0;
  let t1;
  let t2;
  if ($[0] !== props.children) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const code = Children.only(props.children) as ReactElement;
      const codeProps = code.props as ComponentProps<"code">;
      const content = codeProps.children;
      if (typeof content !== "string") {
        t2 = null;
        break bb0;
      }
      let lang = codeProps.className?.split(" ").find(_temp)?.slice(9) ?? "text";
      if (lang === "mdx") {
        lang = "md";
      }
      T0 = DynamicCodeBlock;
      t0 = lang;
      t1 = content.trimEnd();
    }
    $[0] = props.children;
    $[1] = T0;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    T0 = $[1];
    t0 = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  let t3;
  if ($[5] !== T0 || $[6] !== t0 || $[7] !== t1) {
    t3 = <T0 lang={t0} code={t1} />;
    $[5] = T0;
    $[6] = t0;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp(v) {
  return v.startsWith("language-");
}
const processor = createProcessor();
export function Markdown(t0) {
  const $ = _c(7);
  const {
    text
  } = t0;
  const deferredText = useDeferredValue(text);
  let t1;
  if ($[0] !== text) {
    t1 = <p className="invisible">{text}</p>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== deferredText) {
    t2 = <Renderer text={deferredText} />;
    $[2] = deferredText;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Suspense fallback={t1}>{t2}</Suspense>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
const cache = new Map<string, Promise<ReactNode>>();
function Renderer({
  text
}: {
  text: string;
}) {
  const result = cache.get(text) ?? processor.process(text);
  cache.set(text, result);
  return use(result);
}